import { Autocomplete, autocompleteClasses, ListItem, styled } from '@mui/material'
import { ConsoleTextField } from '@/components/inputs/StyledInputComponents'
import { Scrollbar } from '@/components/Scrollbar'

import {
  VISIBLE_AUTOCOMPLETE_DEFAULT_MAX_HEIGHT,
  VISIBLE_AUTOCOMPLETE_TEST_IDS
} from './VisibleAutocomplete.constants'
import { VisibleAutocompleteProps } from './VisibleAutocomplete.types'

const StyledVisibleAutocomplete = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    color: 'inherit',
    width: '100%',
    marginTop: theme.spacing(0.5)
  },
  [`& .${autocompleteClasses.option}`]: {
    minHeight: 'auto',
    padding: theme.spacing(0.75, 1),
    alignItems: 'center',
    width: '100%',
    backgroundColor: 'transparent',
    borderRadius: 4,
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    }
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative'
  }
}))

type PopperComponentProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const PopperComponent = (props: PopperComponentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disablePortal: _1, anchorEl, open: _2, ...other } = props
  return <StyledVisibleAutocomplete {...other} key={anchorEl?.id} />
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const VisibleAutocomplete = <Option extends unknown>({
  options,
  value = [],
  endAdornment,
  filterOptions,
  isOptionEqualToValue,
  maxHeight = VISIBLE_AUTOCOMPLETE_DEFAULT_MAX_HEIGHT,
  noOptionsText = 'No options',
  onChange,
  placeholder = 'Search...',
  getOptionLabel,
  renderOption,
  testId = VISIBLE_AUTOCOMPLETE_TEST_IDS.root
}: VisibleAutocompleteProps<Option>) => {
  return (
    <Autocomplete
      open
      value={value}
      multiple
      onChange={(_e, value) => onChange(value)}
      autoFocus
      filterSelectedOptions
      disableClearable
      autoComplete={false}
      blurOnSelect={false}
      clearOnBlur={false}
      options={options}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={
        renderOption ||
        ((props, option) => (
          <ListItem {...props} data-testid={VISIBLE_AUTOCOMPLETE_TEST_IDS.option(props.key)}>
            {getOptionLabel?.(option)}
          </ListItem>
        ))
      }
      filterOptions={filterOptions}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <ConsoleTextField
          {...params}
          placeholder={placeholder}
          slotProps={{
            input: {
              ...params.InputProps,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              ...({ ['data-testid']: VISIBLE_AUTOCOMPLETE_TEST_IDS.input } as any),
              endAdornment
            }
          }}
        />
      )}
      renderValue={() => null}
      data-testid={testId}
      slots={{
        popper: PopperComponent,
        listbox: (props) => (
          <Scrollbar
            {...props}
            testId={VISIBLE_AUTOCOMPLETE_TEST_IDS.listbox}
            style={{
              height: maxHeight || VISIBLE_AUTOCOMPLETE_DEFAULT_MAX_HEIGHT,
              paddingBottom: '1rem'
            }}
          />
        )
      }}
    />
  )
}
